const UCAN_APP_STORAGE_KEY = 'ucan_app';

const persistIOSApp = () => {
  try {
    localStorage.setItem(UCAN_APP_STORAGE_KEY, 'ios');
  } catch (_) {}
};

const hasIOSMarker = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (typeof window.ReactNativeWebView !== 'undefined') {
    return true;
  }

  if (
    typeof navigator !== 'undefined' &&
    navigator.userAgent &&
    navigator.userAgent.includes('U-CAN-iOS')
  ) {
    return true;
  }

  if (window.__UCAN_IOS_APP__ === true) {
    return true;
  }

  try {
    const params = new URLSearchParams(window.location.search);

    if (params.get('ucan_app') === 'ios') {
      return true;
    }
  } catch (_) {}

  try {
    if (localStorage.getItem(UCAN_APP_STORAGE_KEY) === 'ios') {
      return true;
    }
  } catch (_) {}

  return false;
};

export const isUcanApp = () => {
  const detected = hasIOSMarker();

  if (detected) {
    persistIOSApp();

    if (typeof window !== 'undefined') {
      window.__UCAN_IOS_APP__ = true;
    }
  }

  return detected;
};

export const getUcanAppPath = (path) => {
  if (!isUcanApp()) {
    return path;
  }

  try {
    const url = new URL(path, window.location.origin);

    /*
     * Solo propagamos el marcador a URLs propias de U-CAN.
     * Nunca añadimos ucan_app=ios a dominios externos.
     */
    if (
      url.hostname === 'u-can.cl' ||
      url.hostname === 'www.u-can.cl'
    ) {
      url.searchParams.set('ucan_app', 'ios');

      if (path.startsWith('http://') || path.startsWith('https://')) {
        return url.toString();
      }

      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch (_) {}

  return path;
};

const installIOSNavigationGuard = () => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!isUcanApp()) {
    return;
  }

  if (window.__UCAN_IOS_NAV_GUARD__) {
    return;
  }

  window.__UCAN_IOS_NAV_GUARD__ = true;

  /*
   * Cualquier window.open() hacia una página propia de U-CAN
   * permanece dentro de la misma ventana y conserva el contexto iOS.
   *
   * Así evitamos que target=_blank / window.open abra Safari
   * y transforme accidentalmente la sesión iOS en una sesión web.
   */
  const originalOpen = window.open.bind(window);

  window.open = (url, target, features) => {
    if (!url) {
      return originalOpen(url, target, features);
    }

    try {
      const parsed = new URL(String(url), window.location.origin);

      if (
        parsed.hostname === 'u-can.cl' ||
        parsed.hostname === 'www.u-can.cl'
      ) {
        parsed.searchParams.set('ucan_app', 'ios');

        window.location.href =
          `${parsed.pathname}${parsed.search}${parsed.hash}`;

        return window;
      }

      /*
       * Desde el contexto iOS no permitimos que un window.open()
       * pueda lanzar un checkout web de Mercado Pago/Mercado Libre.
       */
      if (
        parsed.hostname.includes('mercadopago.') ||
        parsed.hostname.includes('mercadolibre.')
      ) {
        console.warn(
          'U-CAN iOS: checkout web externo bloqueado',
          parsed.toString()
        );

        return null;
      }
    } catch (_) {}

    return originalOpen(url, target, features);
  };

  /*
   * También corregimos enlaces HTML propios con target="_blank".
   * No afecta Instagram/Facebook ni otros enlaces externos.
   */
  document.addEventListener(
    'click',
    (event) => {
      const anchor = event.target?.closest?.('a');

      if (!anchor) {
        return;
      }

      const href = anchor.getAttribute('href');

      if (!href) {
        return;
      }

      try {
        const parsed = new URL(href, window.location.origin);

        if (
          parsed.hostname === 'u-can.cl' ||
          parsed.hostname === 'www.u-can.cl'
        ) {
          parsed.searchParams.set('ucan_app', 'ios');

          anchor.setAttribute(
            'href',
            `${parsed.pathname}${parsed.search}${parsed.hash}`
          );

          if (anchor.target === '_blank') {
            event.preventDefault();

            window.location.href =
              `${parsed.pathname}${parsed.search}${parsed.hash}`;
          }
        }
      } catch (_) {}
    },
    true
  );
};

export const initializeUcanAppContext = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const detected = isUcanApp();

  if (detected) {
    persistIOSApp();
    window.__UCAN_IOS_APP__ = true;

    installIOSNavigationGuard();
  }

  return detected;
};

export const clearUcanAppContext = () => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(UCAN_APP_STORAGE_KEY);
      delete window.__UCAN_IOS_APP__;
    } catch (_) {}
  }
};

/*
 * Detecta iOS / iPadOS.
 *
 * iPhone       => true
 * iPad         => true
 * iPadOS       => true
 *
 * MacBook/iMac => false
 * Android      => false
 * Windows      => false
 * Linux        => false
 */
export const isAppleMobileOS = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const touchPoints = Number(navigator.maxTouchPoints || 0);

  // iPhone / iPad / iPod
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return true;
  }

  /*
   * iPadOS moderno puede identificarse como MacIntel.
   * Lo distinguimos de un Mac real por los puntos táctiles.
   */
  if (
    platform === 'MacIntel' &&
    touchPoints > 1
  ) {
    return true;
  }

  return false;
};

/*
 * U-CAN PREMIUM — CONTEXTO APPLE GLOBAL
 *
 * true:
 * - U-CAN iOS App
 * - iPhone
 * - iPad / iPadOS
 * - Mac / macOS
 *
 * false:
 * - Android
 * - Windows
 * - Linux
 *
 * IMPORTANTE:
 * Esto NO significa que un navegador pueda ejecutar StoreKit.
 * Solo determina que en dispositivos Apple jamás debemos ofrecer
 * Mercado Pago ni códigos promocionales para U-CAN Premium.
 */
export const isApplePlatform = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  if (isUcanApp()) {
    return true;
  }

  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';

  // iPhone / iPad / iPod
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return true;
  }

  // macOS / iPadOS moderno identificado como Mac
  if (
    /Macintosh|Mac OS X/i.test(ua) ||
    /MacIntel|MacPPC|Mac68K/i.test(platform)
  ) {
    return true;
  }

  return false;
};


// Comunicación segura con la aplicación nativa U-CAN.
export const sendUcanNativeMessage = (payload) => {
  if (typeof window === 'undefined') return false;

  const bridge = window.ReactNativeWebView;

  if (!bridge || typeof bridge.postMessage !== 'function') {
    console.error('U-CAN: puente nativo no disponible', payload?.type);
    return false;
  }

  try {
    bridge.postMessage(JSON.stringify(payload));
    console.info('U-CAN: mensaje enviado al puente', payload?.type);
    return true;
  } catch (error) {
    console.error('U-CAN: error enviando mensaje nativo', error);
    return false;
  }
};
